package top.wcpe.allincore.dsl.compiler.semantic.`native`

import top.wcpe.allincore.dsl.compiler.ast.ClassDecl
import top.wcpe.allincore.dsl.compiler.ast.ClassKind
import top.wcpe.allincore.dsl.compiler.semantic.`native`.methods.DateApiMethodMap
import top.wcpe.allincore.dsl.compiler.semantic.`native`.methods.LocalApiMethodMap
import top.wcpe.allincore.dsl.compiler.semantic.`native`.methods.NetRequestMethod
import top.wcpe.allincore.dsl.compiler.semantic.`native`.methods.NetSubscribeMethod
import top.wcpe.allincore.dsl.compiler.semantic.`native`.methods.PersistApiMethodMap
import top.wcpe.allincore.dsl.compiler.semantic.`native`.methods.SessionApiMethodMap
import top.wcpe.allincore.dsl.compiler.semantic.`native`.methods.TimerApiMethodMap
import top.wcpe.allincore.dsl.compiler.semantic.`native`.methods.TimeApiMethodMap
import top.wcpe.allincore.dsl.compiler.semantic.`native`.methods.UserObjectMethodDescriptor
import top.wcpe.allincore.dsl.compiler.semantic.`native`.methods.VanillaApiMethodMap

/**
 * 描述符注册中心。
 *
 * 管理内置宿主 object（net / local / vanilla）与用户 class（Stage 2 起接入）的描述符。
 */
class ClassRegistry {

    private val hostObjects: MutableMap<String, HostObjectDescriptor> = linkedMapOf()

    /**
     * Stage 8：嵌套 user object 注册表。
     *
     * key 为点分路径如 "Outer.Inner" / "A.B.C"，value 为对应的嵌套 [ClassDecl]。
     * 用于 [top.wcpe.allincore.dsl.compiler.pipeline.NativeClassResolverPass] 识别
     * `Outer.Inner.foo(...)` 形态的 MemberCall（target = MemberAccess 链），并查嵌套
     * fun 做 lower。
     */
    private val nestedUserObjects: MutableMap<String, ClassDecl> = linkedMapOf()

    /**
     * 注册一个内置宿主 object（如 net / local / vanilla）。
     *
     * 同名重复注册抛 [IllegalStateException] 防御。
     */
    fun registerHostObject(desc: HostObjectDescriptor) {
        check(!hostObjects.containsKey(desc.name)) {
            "宿主 object '${desc.name}' 已注册，不允许重复注册"
        }
        hostObjects[desc.name] = desc
    }

    /**
     * Stage 7a / Stage 8：从 classDecls 中提取 kind = OBJECT 的 ClassDecl，
     * 为顶层 object 注册 [HostObjectDescriptor]，并把嵌套 OBJECT 写入 [nestedUserObjects]
     * 索引（点分路径），供后续 NativeClassResolverPass 在 MemberCall lower 时查用。
     *
     * - 与已注册的内置宿主 object 同名（如 net / local）的顶层 user object 静默跳过；
     * - 嵌套 object 与已注册路径同名时静默跳过。
     */
    fun registerUserObjects(classDecls: List<ClassDecl>) {
        for (decl in classDecls) {
            if (decl.kind != ClassKind.OBJECT) continue
            // 与内置宿主 object 同名（如 net / local）→ 不覆盖内置
            if (hostObjects.containsKey(decl.name)) continue
            val methods = decl.funs.associate { fn ->
                fn.name to UserObjectMethodDescriptor(decl.name, fn) as MethodDescriptor
            }
            hostObjects[decl.name] = HostObjectDescriptor(decl.name, methods)
            // Stage 8：递归把嵌套 user object 注册到 nestedUserObjects
            registerNestedObjects(parentPath = decl.name, nested = decl.nestedClasses)
        }
    }

    /**
     * Stage 8：递归把嵌套 OBJECT 写入 [nestedUserObjects]，key 用点分路径。
     *
     * - DATA kind 的 nested 直接跳过（保持 Stage 8 仅支持 OBJECT 嵌套调用的边界）
     * - 已注册路径再次出现时静默跳过，与顶层 [registerUserObjects] 防御策略保持一致
     */
    private fun registerNestedObjects(parentPath: String, nested: List<ClassDecl>) {
        for (decl in nested) {
            if (decl.kind != ClassKind.OBJECT) continue
            val path = "$parentPath.${decl.name}"
            if (nestedUserObjects.containsKey(path)) continue
            nestedUserObjects[path] = decl
            registerNestedObjects(path, decl.nestedClasses)
        }
    }

    /** 按名查找宿主 object；未注册返回 null。 */
    fun lookupHostObject(name: String): HostObjectDescriptor? = hostObjects[name]

    /** 已注册的所有宿主 object 名（用于诊断与调试）。 */
    fun allHostObjectNames(): Set<String> = hostObjects.keys.toSet()

    /** Stage 8：按点分路径（如 "Outer.Inner"）查找嵌套 user object；未注册返回 null。 */
    fun lookupNestedObject(path: String): ClassDecl? = nestedUserObjects[path]

    /** Stage 8：已注册的所有嵌套 user object 路径（用于诊断与调试）。 */
    fun allNestedObjectPaths(): Set<String> = nestedUserObjects.keys.toSet()

    companion object {
        /**
         * 创建一个内置已注册的默认 registry，包含 net / local / vanilla 三个宿主 object。
         */
        fun withBuiltins(): ClassRegistry {
            val registry = ClassRegistry()
            registry.registerHostObject(
                HostObjectDescriptor(
                    name = "net",
                    methods = mapOf(
                        NetRequestMethod.name to NetRequestMethod,
                        NetSubscribeMethod.name to NetSubscribeMethod,
                    ),
                )
            )
            registry.registerHostObject(
                HostObjectDescriptor(
                    name = "local",
                    methods = LocalApiMethodMap,
                )
            )
            registry.registerHostObject(
                HostObjectDescriptor(
                    name = "vanilla",
                    methods = VanillaApiMethodMap,
                )
            )
            registry.registerHostObject(
                HostObjectDescriptor(
                    name = "time",
                    methods = TimeApiMethodMap,
                )
            )
            registry.registerHostObject(
                HostObjectDescriptor(
                    name = "date",
                    methods = DateApiMethodMap,
                )
            )
            registry.registerHostObject(
                HostObjectDescriptor(
                    name = "session",
                    methods = SessionApiMethodMap,
                )
            )
            registry.registerHostObject(
                HostObjectDescriptor(
                    name = "persist",
                    methods = PersistApiMethodMap,
                )
            )
            registry.registerHostObject(
                HostObjectDescriptor(
                    name = "timer",
                    methods = TimerApiMethodMap,
                )
            )
            return registry
        }
    }
}
